import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Copy JSON to clipboard button. Reusable across admin debug surfaces
 * (Raw user document, Raw answers, etc). Falls back to a select-all
 * textarea when the Clipboard API is unavailable (e.g. non-secure
 * contexts, older browsers).
 */
export const CopyJsonButton = ({ value, label = 'Copy JSON', testId }) => {
  const [copied, setCopied] = useState(false);

  const flashCopied = () => {
    setCopied(true);
    toast.success('Copied!');
    setTimeout(() => setCopied(false), 1500);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const text = typeof value === 'string'
      ? value
      : JSON.stringify(value, null, 2);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        flashCopied();
        return;
      }
      throw new Error('clipboard-unavailable');
    } catch (_) {
      // Fallback: temporary textarea + execCommand('copy').
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        flashCopied();
      } catch (_e2) {
        toast.error('Could not copy. Select the text manually.');
      }
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-700 text-[11px] font-semibold px-2 py-1"
      data-testid={testId || 'copy-json-btn'}
      aria-label={label}
    >
      {copied
        ? <Check className="h-3 w-3 text-emerald-600" />
        : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
};

export default CopyJsonButton;
